import React, { useState, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { resizeImage } from './resizeImage';

export default function ProfileSetupBanner() {
  const { name, setname, setpicture, infoLoaded } = useAuth();

  const [dismissed, setDismissed] = useState(() => {
    const val = localStorage.getItem('sp_profile_setup_dismissed');
    if (!val) return false;
    if (val === 'done') return true;
    return Date.now() - parseInt(val) < 24 * 60 * 60 * 1000;
  });

  const [inputName, setInputName] = useState('');
  const [previewPic, setPreviewPic] = useState(null);
  const [picBase64, setPicBase64] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  if (!infoLoaded || name !== 'User' || dismissed) return null;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB.'); return; }
    try {
      const resized = await resizeImage(file);
      setPreviewPic(resized);
      setPicBase64(resized);
    } catch {
      setError('Could not process image. Please try another file.');
    }
  };

  const handleSave = async () => {
    if (!inputName.trim()) { setError('Please enter your name.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/update-profile', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: inputName.trim(), picture_base64: picBase64 || null }),
      });
      const data = await res.json();
      if (res.ok && data.code === 200) {
        setname(inputName.trim());
        if (data.picture_url) setpicture(data.picture_url);
        localStorage.setItem('sp_profile_setup_dismissed', 'done');
        setDismissed(true);
      } else {
        setError(data.message || 'Failed to save. Please try again.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLater = () => {
    localStorage.setItem('sp_profile_setup_dismissed', Date.now().toString());
    setDismissed(true);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      border: '1px solid #bae6fd',
      borderRadius: 14,
      padding: '20px 24px',
      marginBottom: 24,
      display: 'flex',
      alignItems: 'center',
      gap: 20,
      flexWrap: 'wrap',
    }}>
      {/* Clickable avatar upload */}
      <div
        onClick={() => fileRef.current.click()}
        title="Click to upload a profile photo"
        style={{
          width: 64, height: 64, borderRadius: '50%',
          background: '#e0f2fe', flexShrink: 0,
          overflow: 'hidden', cursor: 'pointer',
          border: '2px dashed #38bdf8',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {previewPic
          ? <img src={previewPic} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontSize: 26 }}>👤</span>
        }
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>

      {/* Text + inputs */}
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 2 }}>
          👋 Welcome! Let's set up your profile
        </div>
        <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: 10 }}>
          Add your name so your emails and notifications are personalised.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Your full name"
            value={inputName}
            onChange={e => setInputName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            style={{
              border: '1px solid #bae6fd', borderRadius: 8,
              padding: '7px 12px', fontSize: '0.88rem',
              outline: 'none', background: '#fff', minWidth: 180,
            }}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#0ea5e9', color: '#fff', border: 'none',
              borderRadius: 8, padding: '7px 18px',
              fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={handleLater}
            style={{
              background: 'transparent', color: '#64748b', border: 'none',
              fontSize: '0.82rem', cursor: 'pointer', padding: '7px 4px',
              textDecoration: 'underline',
            }}
          >
            Remind me later
          </button>
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 6 }}>{error}</div>}
      </div>

      <div style={{ fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }}>
        Click the circle to upload a photo
      </div>
    </div>
  );
}
